/**
 * Sends a /ga/session request to the old express backend which
 * will create session info and store it in the redis database
 * so that it can push a purchase event to GA4 when it receives
 * a webhook from Centra.
 *
 * 1. If no consent is given - use the gtag() macro
 * 2. If consent is given - use the _ga cookie
 * 3. Always send the request to the backend, regardless of ID existence
 */
import { FrontendContextV1 } from "@made-people/nestjs-bff-service";
import { defaultWhitelistedQueryParamKeys } from "@made-people/grease-link";
import { createMmBackendClient } from "./create-mm-backend-client";
import Cookies from "js-cookie";

// Production GA4 ID
const GA4_ID = "G-3PPYTRJV8E";

type ClientIdSource = "gtag" | "cookie";

type GtagWindow = Window & {
  gtag?: (
    command: "get",
    target: string,
    field: string,
    callback: (value?: string) => void,
  ) => void;
};

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const id = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(id);
      reject(signal.reason);
    };
    if (signal.aborted) {
      onAbort();
    } else {
      signal.addEventListener("abort", onAbort, { once: true });
    }
  });

const gtagGet = (field: string, timeoutMs = 500) =>
  new Promise<string | undefined>((resolve) => {
    const gtag = (window as GtagWindow).gtag;
    if (typeof gtag !== "function") {
      resolve(undefined);
      return;
    }

    const timeout = window.setTimeout(() => resolve(undefined), timeoutMs);
    const settle = (value?: string) => {
      window.clearTimeout(timeout);
      resolve(value);
    };
    try {
      gtag("get", GA4_ID, field, settle);
    } catch {
      settle(undefined);
    }
  });

const readGaCookie = () => {
  // _ga cookie value looks like `GA1.1.1234567890.1234567890`; the client
  // id is everything after the second dot.
  const value = Cookies.get("_ga");
  return value?.match(/^GA\d+\.\d+\.([\d.]+)$/)?.[1];
};

/**
 * Polls for the GA `session_id` and `client_id` until both are found or
 * `attempts` is exhausted. `priority` controls which client_id source —
 * `_ga` cookie or `gtag('get', ..., 'client_id')` — is checked first; the
 * other is still tried as a fallback within the same attempt.
 */
async function resolveGaIds(
  priority: ClientIdSource,
  signal: AbortSignal,
  attempts = 10,
  delayMs = 500,
) {
  let sessionId: string | undefined;
  let clientId: string | undefined;

  const gtagClientId = () => gtagGet("client_id", delayMs);
  const sources =
    priority === "cookie"
      ? [readGaCookie, gtagClientId]
      : [gtagClientId, readGaCookie];

  for (let i = 0; i < attempts; i++) {
    signal.throwIfAborted();
    sessionId ||= await gtagGet("session_id", delayMs);
    for (const get of sources) {
      if (clientId) {
        break;
      }
      clientId = (await get()) || undefined;
    }
    if (sessionId && clientId) {
      break;
    }
    if (i < attempts - 1) {
      await sleep(delayMs, signal);
    }
  }

  return { sessionId, clientId };
}

/**
 * Strip everything from the URL except the whitelisted attribution params
 * (utm_*, gclid, fbclid, ttclid, msclkid, _kx, dema_id, ...) so the backend
 * can attribute the GA4 purchase event correctly. Returns undefined when
 * the page isn't a landing page (no whitelisted params present).
 */
function buildLandingUrl(url: URL): string | undefined {
  const entries = defaultWhitelistedQueryParamKeys.flatMap((key) => {
    const value = url.searchParams.get(key);
    return value !== null ? [[key, value] as const] : [];
  });
  if (entries.length === 0) {
    return undefined;
  }

  const landing = new URL(`${url.origin}${url.pathname}`);
  for (const [k, v] of entries) {
    landing.searchParams.set(k, v);
  }
  return landing.toString();
}

export function gaCreateSession(
  token: string,
  frontendContext: FrontendContextV1,
) {
  if (typeof window === "undefined") {
    return;
  }

  const backendClient = createMmBackendClient(frontendContext);
  let activeController: AbortController | undefined;

  async function send(priority: ClientIdSource) {
    // Cancel any in-flight attempt (retry loop + network request) before
    // starting a new one so we never race two `/ga/session` writes.
    activeController?.abort();
    const controller = (activeController = new AbortController());
    const { signal } = controller;

    try {
      const { sessionId, clientId } = await resolveGaIds(priority, signal);
      signal.throwIfAborted();

      await backendClient.put(
        `/ga/session`,
        {
          url: buildLandingUrl(new URL(window.location.href)),
          gaClientId: clientId,
          gaSessionId: sessionId,
          selectionId: token,
          gbuuid: Cookies.get("gbuuid"),
          headers: {
            referer: document.referrer,
            "user-agent": navigator.userAgent,
          },
        },
        { signal },
      );
    } catch (error) {
      // Both our throwIfAborted and axios's CanceledError only fire when
      // the signal was aborted, so this single check covers both.
      if (signal.aborted) {
        return;
      }
      console.error("GA: Failed to set trackingInfo", error);
    }
  }

  // Always make at least one call per page load. If consent is already in
  // place, prefer the `_ga` cookie immediately since gtag may not be
  // initialised yet but the cookie is already populated from a prior visit.
  void send(window.Cookiebot?.consent?.statistics ? "cookie" : "gtag");

  // Re-run with cookie precedence once consent is confirmed. Cookiebot also
  // re-fires this on every load for consenting users; the abort in `send`
  // makes the duplicate harmless (the second call cancels and supersedes).
  window.addEventListener("CookiebotOnAccept", () => void send("cookie"));
}
